import '@vimeo/polyfills/replaceAll.polyfill';
import 'regenerator-runtime/runtime';

import isPropValid from '@emotion/is-prop-valid';
import { NextComponentType, NextPageContext, NextPage as _NextPage } from 'next';
import { AppProps as NextAppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { ReactNode, StrictMode, useRef, useState, useEffect, useMemo } from 'react';
import styled, { StyleSheetManager } from 'styled-components';

import { GooglePublisherTags as GPT } from '@vpr/ads';
import { camelizeDeep } from '@vpr/restful';
import { FatalAttraction as FA, GoogleTagManager as GTM } from '@vpr/tracking';

import { createProvider as createActivityProvider } from '@vimeo/activity';
import { BigPictureClient, Event } from '@vimeo/bigpicture-client';
import { PlayerAssetUrls } from '@vimeo/core/AppTypes';
import { ErrorBoundary } from '@vimeo/core/components/ErrorBoundary';
import { GlobalStyles } from '@vimeo/core/components/GlobalStyles';
import { ThemeProvider } from '@vimeo/core/components/ThemeProvider';
import { CacheContext } from '@vimeo/core/hooks/use-api-resource';
import { Cache } from '@vimeo/core/libs/cache';
import { bootstrapBigPicture, useDatadogSetUser } from '@vimeo/core/libs/tracking';
import { GctlProvider } from '@vimeo/gctl-api/hooks/GctlProvider';
import { PicoProvider } from '@vimeo/picox';
import type { ErrorPageProps } from '@vimeo/ui/core/ErrorPage';
import { GlobalBanners } from '@vimeo/ui/core/GlobalBanners';
import LoadingPage from '@vimeo/ui/core/LoadingPage';
import { Viewer, ViewerContext } from '@vimeo/viewer';

import { PageAuthenticationFunction } from '../utils/pageAuth';

const Modworld = dynamic(() => import('@vimeo/modworld'), { ssr: false });

const ErrorPage = dynamic<ErrorPageProps>(() =>
  import('@vimeo/layouts/ErrorPageWithHeader').then((m) => m.ErrorPageWithHeader),
);

type Overwrite<A, B> = Omit<A, keyof B> & B;

export type PageProps = {
  staffOnly?: boolean;
  requireLogin?: boolean;
  /** Metadata configuration for this page. */
  metadata?: {
    /** Any arguments to pass to the PHP metadata class */
    args?: Record<string, string>;
    /** The name of the PHP metadata class that renders the metadata for this page */
    className: string;
    /** The route rule for the page. Used to extract route arguments in the
     * metadata class. */
    routeRule?: string;
  };
  isIframe?: boolean;
  hasThemeSupport?: boolean;
  layoutOptions?: {
    headerFixed?: boolean;
    globalNotifications?: boolean;
  };
  playerAssetUrls: PlayerAssetUrls;
  reactRouter?: Record<
    string,
    {
      staffOnly?: boolean;
      requireLogin?: boolean;
    }
  >;
  [key: string]: any;
};

export type GetLayoutFunction<P = {}> = (children: ReactNode, props: PageProps & P) => ReactNode;

type OptionalPageAttributes<P = {}> = {
  authenticate?: PageAuthenticationFunction;
  getLayout?: GetLayoutFunction<P>;
};

export type NextPage<P = {}> = _NextPage<Partial<PageProps> & P> & OptionalPageAttributes<P>;

type AppComponentType<P = {}> = NextComponentType<NextPageContext, any, P> & OptionalPageAttributes;

type AppProps<P = {}> = Overwrite<
  NextAppProps<P>,
  {
    Component: AppComponentType<PageProps>;
    locale: string;
    pageProps: PageProps;
  }
>;

// Timer values
const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const FOURTEEN_MINUTES = 14 * ONE_MINUTE;

const onClient = typeof window !== 'undefined';

function trackPageView(url: string, lastRoute?: [referrerPathname: string, referrer: string]) {
  let [, referrer] = lastRoute ?? [null, document.referrer];

  BigPictureClient.sendEvent(
    new Event('pageview', 4, {
      referrer,
      url: document.URL,
    }),
  );
  FA.trackPageView();

  // Third party tracking
  GTM.addData({ page: url });
  GTM.trackEvent('pageview');
  GPT.queue((gtag) => {
    gtag.pubads().updateCorrelator();
  });
}

const ActivityProvider = createActivityProvider({
  activities: [],
  noOlderActivitiesExist: false,
  isRequestingOlderActivities: true,
  shouldIndicateUndisplayedActivities: false,
  pendingFollows: {},
});

let viewerPromise: null | Promise<Viewer> = null;
if (typeof window !== 'undefined') {
  // start fetching as we render the app
  viewerPromise = fetch('/_next/viewer')
    .then((res) => res.json())
    .then(camelizeDeep);
}

export default function AppWrapper({ Component, pageProps }: AppProps) {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const lastPageRef = useRef<[pathnanme: string, asPath: string] | undefined>(undefined);
  const router = useRouter();
  const cache = useRef(new Cache({ maxSize: 512 }));
  const jwtRefreshTimer = useRef<number | undefined>(undefined);

  const authenticated = Component.authenticate?.(viewer?.user, router) ?? true;

  const viewerId = viewer?.user?.id?.toString() ?? null;
  const vuid = viewer?.vuid ?? null;

  const picoIdentifiers = useMemo(
    () => ({
      user_id: viewerId,
      vuid,
    }),
    [viewerId, vuid],
  );

  useDatadogSetUser(viewer);

  // on mount
  useEffect(() => {
    viewerPromise?.then((_viewer) => {
      setViewer({ ..._viewer, setUser: (user) => setViewer((viewer) => ({ ...viewer!, user })) });
      // Add viewer to window for legacy packages
      window.bootstrap_data = { viewer: _viewer };

      // eslint-disable-next-line no-eval
      _viewer.gdprBannerJS && eval(_viewer.gdprBannerJS);
      // eslint-disable-next-line no-eval
      _viewer.vuidCookieJS && eval(_viewer.vuidCookieJS);

      // init tracking
      bootstrapBigPicture(_viewer);
      FA.init(window.__fa);
      GTM.init(window._gtm ?? []);

      // if the user has opted out of mobile set the viewport so that responsive styles do not activate
      if (_viewer?.optOutMobile) {
        const viewport: HTMLElement | null = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=1200');
        } else {
          // will only log in dev
          console.log('Warning: Tried to set viewport, but viewport was not found');
        }
      }

      trackPageView(router.pathname);
    });
  }, []);

  // token refresh
  useEffect(() => {
    if (!viewer) {
      return;
    }

    function queueTokenRefresh() {
      jwtRefreshTimer.current = setTimeout(async () => {
        const data = await fetch('/_next/jwt', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        const { token: jwt } = await data.json();

        setViewer((prev) => prev && { ...prev, jwt });

        queueTokenRefresh();
      }, FOURTEEN_MINUTES);
    }

    queueTokenRefresh();

    return () => clearTimeout(jwtRefreshTimer.current);
  }, [viewer]);

  useEffect(() => {
    const win = window as any;
    if (win.title) {
      setTitle(win.title);
    }
  }, [pageProps.metadata, router.pathname]);
  useEffect(() => {
    const onRouteChangeComplete = (url: string) => {
      trackPageView(url, lastPageRef.current);
      lastPageRef.current = [router.pathname, router.asPath];
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [lastPageRef, router]);

  let child;
  let vimeoPageProps = useMemo(() => {
    return {
      ...pageProps,
      playerAssetUrls:
        typeof window !== 'undefined'
          ? window.playerAssetUrls
          : {
              barebone_js: '',
              chromeless_css: '',
              chromeless_js: '',
              css: '',
              js: '',
              player_url: '',
            },
    };
  }, [pageProps]);

  // If viewer has not loaded yet then we are hitting the app for the first time
  // in which case the server has already handled authenticating this route
  if (onClient && viewer && !authenticated) {
    child = <LoadingPage />;
    router.reload();
  } else if (Component.getLayout) {
    child = Component.getLayout(<Component {...vimeoPageProps} />, vimeoPageProps);
  } else {
    child = <Component {...vimeoPageProps} />;
  }

  const canUseModWorld =
    viewer?.user?.capabilities?.canUseModWorld && onClient && window === window.parent;

  return (
    <StyleSheetManager
      shouldForwardProp={(prop, target) => (typeof target === 'string' ? isPropValid(prop) : true)}
    >
      <Head>
        {pageProps.hasThemeSupport && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
var theme=JSON.parse(window.localStorage.getItem("theme-preference"));null===theme&&(theme=window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light"),"dark"===theme&&(document.body.style.background="rgb(18, 18, 18)");
        `,
            }}
          />
        )}
        {title && <title>{title}</title>}
      </Head>
      <GctlProvider
        xVimeoPage={router.pathname}
        jwt={viewer?.jwt}
        baseUrl={viewer ? `https://${viewer.apiUrl}` : ''}
        locale={viewer?.locale}
      >
        <ViewerContext.Provider value={viewer}>
          <CacheContext.Provider value={cache.current}>
            <ActivityProvider token={viewer ? viewer.xsrft : ''}>
              <PicoProvider identifiers={viewer !== null ? picoIdentifiers : null}>
                <ThemeProvider hasThemeSupport={pageProps.hasThemeSupport}>
                  <GlobalStyles />
                  <ErrorBoundary errorPage={ErrorPage} customAttributes={{ page: router.pathname }}>
                    {canUseModWorld && <Modworld />}
                    <GlobalBanners
                      globalNotifications={pageProps.layoutOptions?.globalNotifications}
                      fixed={pageProps.layoutOptions?.headerFixed}
                    />
                    {/* wrap_content is currently used to tell the mod_panel where to be injected in the page
                          see: web/assets/js/apps/modworld/modules/mod_panel.js
                      */}
                    <div className="wrap_content" />
                    <Container>{child}</Container>
                  </ErrorBoundary>
                </ThemeProvider>
              </PicoProvider>
            </ActivityProvider>
          </CacheContext.Provider>
        </ViewerContext.Provider>
      </GctlProvider>
    </StyleSheetManager>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;
